import { Button, Group, NumberInput, Select, Stack, Textarea } from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconPlus } from "@tabler/icons-react";
import { type JSX, useState } from "react";
import { useMutation } from "urql";
import { useSiteConfig } from "../SiteConfig";
import { type TtsBlockCreateMutation, type TtsBlockCreateMutationVariables, ttsBlockCreateMutation } from "./queries";
import { CUSTOM_SILENCE_OPTION, DEFAULT_SILENCE_AFTER, SILENCE_OPTIONS, clampSilence } from "./silenceOptions";

interface IProps {
    projectId: number;
    onCreated: () => void;
}

function TtsBlockForm({ projectId, onCreated }: IProps): JSX.Element {
    const config = useSiteConfig();
    const defaultVoice = config.ttsVoices[0] ?? "alloy";
    const [silenceAfterOption, setSilenceAfterOption] = useState<string>(DEFAULT_SILENCE_AFTER);
    const [customSilenceAfter, setCustomSilenceAfter] = useState<string>(DEFAULT_SILENCE_AFTER);

    const [createResult, executeCreate] = useMutation<TtsBlockCreateMutation, TtsBlockCreateMutationVariables>(
        ttsBlockCreateMutation,
    );

    const form = useForm({
        initialValues: {
            text: "",
            voice: defaultVoice,
            instructions: "",
            silenceAfter: DEFAULT_SILENCE_AFTER,
        },
        validate: {
            text: (value) => (value.trim() ? null : "Text is required"),
        },
    });

    const handleSilenceAfterSelectChange = (value: string | null) => {
        const nextValue = value ?? "";
        if (nextValue === CUSTOM_SILENCE_OPTION) {
            setSilenceAfterOption(CUSTOM_SILENCE_OPTION);
            setCustomSilenceAfter(form.values.silenceAfter);
            return;
        }
        if (nextValue === "") {
            return;
        }
        setSilenceAfterOption(nextValue);
        setCustomSilenceAfter(nextValue);
        form.setFieldValue("silenceAfter", nextValue);
    };

    const handleCustomSilenceAfterChange = (value: string | number) => {
        const clampedString = clampSilence(value);
        if (clampedString === null) {
            setCustomSilenceAfter(value === undefined || value === null ? "" : String(value));
            return;
        }
        setSilenceAfterOption(CUSTOM_SILENCE_OPTION);
        setCustomSilenceAfter(clampedString);
        form.setFieldValue("silenceAfter", clampedString);
    };

    const handleSubmit = async (values: typeof form.values) => {
        const res = await executeCreate({
            projectId,
            data: {
                text: values.text.trim(),
                voice: values.voice,
                instructions: values.instructions || null,
                silenceAfter: parseFloat(values.silenceAfter),
            },
        });
        if (res.error) {
            notifications.show({ title: "Failed to add block", message: res.error.message });
        } else {
            form.reset();
            setSilenceAfterOption(DEFAULT_SILENCE_AFTER);
            setCustomSilenceAfter(DEFAULT_SILENCE_AFTER);
            onCreated();
        }
    };

    return (
        <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="sm">
                <Textarea
                    label="Text"
                    placeholder="Enter text to convert to speech..."
                    autosize
                    minRows={3}
                    maxRows={10}
                    {...form.getInputProps("text")}
                />
                <Group grow>
                    <Select
                        label="Voice"
                        data={config.ttsVoices.map((v) => ({ value: v, label: v }))}
                        {...form.getInputProps("voice")}
                    />
                    <Select
                        label="Silence After"
                        data={[...SILENCE_OPTIONS, { value: CUSTOM_SILENCE_OPTION, label: "Custom" }]}
                        value={silenceAfterOption}
                        onChange={handleSilenceAfterSelectChange}
                    />
                    {silenceAfterOption === CUSTOM_SILENCE_OPTION ? (
                        <NumberInput
                            label="Custom silence (seconds)"
                            value={customSilenceAfter}
                            onChange={handleCustomSilenceAfterChange}
                            min={0}
                            max={9.99}
                            step={0.1}
                            decimalScale={2}
                            allowNegative={false}
                        />
                    ) : null}
                </Group>
                <Textarea
                    label="Instructions"
                    placeholder="Optional: accent, tone, speed, emotional range..."
                    autosize
                    minRows={2}
                    maxRows={4}
                    {...form.getInputProps("instructions")}
                />
                <Group justify="flex-end" mt="xs">
                    <Button
                        type="submit"
                        leftSection={<IconPlus size={16} />}
                        loading={createResult.fetching}
                        fullWidth
                    >
                        Add Block
                    </Button>
                </Group>
            </Stack>
        </form>
    );
}

export default TtsBlockForm;
